import * as React from 'react';

import type {GetUserNames_users as UsersConnection} from 'nutshell-graphql-types';

import {StackLegacy} from '../../layout/stack/stack-legacy';
import type {SalesDocumentType} from '../../lead-sidebar/sales-documents/types';
import {SelectPickerGeneric} from '../../select-picker-generic/select-picker-generic';
import {TextField} from '../../textfield/textfield';
import {Body} from '../../typography/body';

import styles from './quote-prepared-by.module.css';

type Props = {
    type: SalesDocumentType;
    preparedBy: string;
    preparedTime: string;
    quoteNumber: string;
    validUntilDate: string | null | undefined;
    users: UsersConnection;
    purchaseOrderNumber?: string | null | undefined;
    jobNumber?: string | null | undefined;
    customerNumber?: string | null | undefined;
    referenceNumber?: string | null | undefined;
    onPreparedByChange: (preparedBy: string) => void;
    onPreparedTimeChange: (preparedTime: string) => void;
    onValidUntilDateChange: (validUntilDate?: string | null | undefined) => void;
    onPurchaseOrderNumberChange: (purchaseOrderNumber?: string | null | undefined) => void;
    onJobNumberChange: (jobNumber?: string | null | undefined) => void;
    onCustomerNumberChange: (customerNumber?: string | null | undefined) => void;
    onReferenceNumberChange: (referenceNumber?: string | null | undefined) => void;
};

export function QuotePreparedBy(props: Props) {
    const formattedOptions = props.users.edges.map((user) => ({
        value: user.node.name,
        label: user.node.name,
    }));

    // @ts-expect-error
    const handleSelectChange = (selectedUser) => {
        props.onPreparedByChange(selectedUser.value);
    };

    // @ts-expect-error
    const handleDateChange = (event, dateType) => {
        if (!event.target.value) {
            if (dateType === 'prepared') {
                event.target.value = props.preparedTime;
            } else if (dateType === 'valid') {
                event.target.value = props.validUntilDate;
            }

            return;
        }

        if (dateType === 'prepared') {
            props.onPreparedTimeChange(event.target.value);
        } else if (dateType === 'valid') {
            props.onValidUntilDateChange(event.target.value);
        }
    };

    // @ts-expect-error
    const preventKeyInput = (e) => {
        e.preventDefault();
    };

    const displayName = props.type === 'Invoice' ? 'Invoice' : 'Quote';

    return (
        <div className='full-width'>
            <StackLegacy spacing={12}>
                <div className='flex flex-dir-col gap-8'>
                    <Body>{`${displayName} number`}</Body>
                    <div className='border border-box br-3 background-offwhite px-8 py-8'>
                        <Body>{props.quoteNumber}</Body>
                    </div>
                </div>
                {props.purchaseOrderNumber != null && (
                    <div className='flex flex-dir-col gap-8'>
                        <Body>Purchase order #</Body>
                        <TextField
                            value={props.purchaseOrderNumber}
                            onChange={(e) => props.onPurchaseOrderNumberChange(e)}
                        />
                    </div>
                )}
                {props.jobNumber != null && (
                    <div className='flex flex-dir-col gap-8'>
                        <Body>Job #</Body>
                        <TextField
                            value={props.jobNumber}
                            onChange={(e) => props.onJobNumberChange(e)}
                        />
                    </div>
                )}
                {props.customerNumber != null && (
                    <div className='flex flex-dir-col gap-8'>
                        <Body>Customer #</Body>
                        <TextField
                            value={props.customerNumber}
                            onChange={(e) => props.onCustomerNumberChange(e)}
                        />
                    </div>
                )}
                {props.referenceNumber != null && (
                    <div className='flex flex-dir-col gap-8'>
                        <Body>Reference #</Body>
                        <TextField
                            value={props.referenceNumber}
                            onChange={(e) => props.onReferenceNumberChange(e)}
                        />
                    </div>
                )}
                <div className='flex flex-dir-col gap-8'>
                    <Body>Prepared by</Body>
                    <SelectPickerGeneric
                        options={formattedOptions}
                        placeholder={props.preparedBy}
                        value={formattedOptions.find((opt) => opt.value === props.preparedBy)}
                        onChange={handleSelectChange}
                    />
                </div>
                <div className='flex flex-dir-col gap-8'>
                    <Body>{props.type === 'Invoice' ? 'Date issued' : 'Prepared on'}</Body>
                    <input
                        type='date'
                        value={props.preparedTime}
                        // @ts-expect-error
                        max={props.validUntilDate}
                        onChange={(e) => handleDateChange(e, 'prepared')}
                        onKeyDown={preventKeyInput}
                        className={styles['date-input']}
                    />
                </div>
                <div className='flex flex-dir-col gap-8'>
                    <Body>{props.type === 'Invoice' ? 'Due date' : 'Valid through'}</Body>
                    <input
                        type='date'
                        // @ts-expect-error
                        value={props.validUntilDate}
                        min={props.preparedTime}
                        onChange={(e) => handleDateChange(e, 'valid')}
                        onKeyDown={preventKeyInput}
                        className={styles['date-input']}
                    />
                </div>
            </StackLegacy>
        </div>
    );
}
